import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorMessage } from '@/lib/api';
import {
  Badge,
  Button,
  Card,
  ErrorText,
  Input,
  Pagination,
  Select,
  formatSum,
  type Localized,
} from '@/components/ui';

interface ProductRow {
  id: string;
  code: string;
  name: Localized;
  pricePerDay: number;
  depositAmount: number | null;
  isActive: boolean;
  isNew: boolean;
  isPopular: boolean;
  variantsCount: number;
  category: { id: string; name: Localized };
  image: { thumbUrl: string } | null;
}

interface CategoryNode {
  id: string;
  level: number;
  name: Localized;
  children: CategoryNode[];
}

export default function ProductsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [q, setQ] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [error, setError] = useState<string | null>(null);

  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => (await api.get<CategoryNode[]>('/admin/categories')).data,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['products', q, categoryId, page, limit],
    queryFn: async () =>
      (
        await api.get<{ items: ProductRow[]; total: number; pages: number }>('/admin/products', {
          params: { q: q || undefined, categoryId: categoryId || undefined, page, limit },
        })
      ).data,
    placeholderData: keepPreviousData,
  });

  const create = useMutation({
    mutationFn: async () => {
      const firstCategory = flatten(categories)[0];
      if (!firstCategory) throw new Error('Сначала создайте хотя бы одну категорию');

      const { data: product } = await api.post<{ id: string }>('/admin/products', {
        categoryId: firstCategory.id,
        name: { ru: 'Новый товар' },
        pricePerDay: 0,
        isActive: false,
      });

      return product;
    },
    onSuccess: (product) => {
      void queryClient.invalidateQueries({ queryKey: ['products'] });
      navigate(`/products/${product.id}`);
    },
    onError: (e) => setError(errorMessage(e)),
  });

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Товары</h1>
          <p className="mt-1 text-sm text-neutral-500">
            {data ? `Всего ${data.total}` : 'Загрузка…'}
          </p>
        </div>
        <Button disabled={create.isPending} onClick={() => create.mutate()}>
          Новый товар
        </Button>
      </header>

      {error && <ErrorText>{error}</ErrorText>}

      <div className="flex flex-wrap gap-3">
        <Input
          placeholder="Поиск по названию и описанию"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
          className="max-w-xs"
        />
        <Select
          value={categoryId}
          onChange={(e) => {
            setCategoryId(e.target.value);
            setPage(1);
          }}
          className="max-w-xs"
        >
          <option value="">Все категории</option>
          {flatten(categories).map((c) => (
            <option key={c.id} value={c.id}>
              {'— '.repeat(c.level - 1)}
              {c.name.ru}
            </option>
          ))}
        </Select>
      </div>

      <Card>
        {isLoading ? (
          <p className="text-sm text-neutral-500">Загрузка…</p>
        ) : !data || data.items.length === 0 ? (
          <p className="text-sm text-neutral-500">Ничего не найдено</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-neutral-500">
                <tr>
                  <th className="pb-2 font-medium">Товар</th>
                  <th className="pb-2 font-medium">Категория</th>
                  <th className="pb-2 font-medium">Цена/сут</th>
                  <th className="pb-2 font-medium">Залог</th>
                  <th className="pb-2 font-medium">Вариаций</th>
                  <th className="pb-2 font-medium">Статус</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((product) => (
                  <tr
                    key={product.id}
                    className="border-t border-neutral-100 dark:border-neutral-800"
                  >
                    <td className="py-2">
                      <Link
                        to={`/products/${product.id}`}
                        className="flex items-center gap-3 hover:underline"
                      >
                        {product.image ? (
                          <img
                            src={product.image.thumbUrl}
                            alt=""
                            className="size-10 rounded object-cover"
                          />
                        ) : (
                          <span className="size-10 rounded bg-neutral-100 dark:bg-neutral-800" />
                        )}
                        <span>
                          <span className="block">{product.name.ru}</span>
                          <span className="block text-xs text-neutral-400">{product.code}</span>
                        </span>
                      </Link>
                    </td>
                    <td className="py-2 text-neutral-500">{product.category.name.ru}</td>
                    <td className="py-2">{formatSum(product.pricePerDay)}</td>
                    <td className="py-2">{formatSum(product.depositAmount)}</td>
                    <td className="py-2">{product.variantsCount}</td>
                    <td className="space-x-1 py-2">
                      {product.isActive ? (
                        <Badge tone="green">вкл</Badge>
                      ) : (
                        <Badge tone="red">выкл</Badge>
                      )}
                      {product.isNew && <Badge>новинка</Badge>}
                      {product.isPopular && <Badge>хит</Badge>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {data && (
        <Pagination
          page={page}
          pages={data.pages}
          total={data.total}
          limit={limit}
          onPage={setPage}
          onLimit={(next) => {
            setLimit(next);
            setPage(1);
          }}
        />
      )}
    </div>
  );
}

function flatten(nodes: CategoryNode[]): CategoryNode[] {
  return nodes.flatMap((node) => [node, ...flatten(node.children)]);
}
